import { useCallback, useEffect, useState } from "react";
import { getMessaging, onMessage } from "firebase/messaging";
import { useLocation, useNavigate } from "react-router-dom";
import app from "../../../firebaseConfig";
import { isFcmSupported, syncFcmToken } from "../../../Actions/FcmService";
import {
  NOTIFICATION_CAMPAIGN_PARAM,
  NOTIFICATION_CLICKED_MESSAGE,
  NotificationEvent,
  reportNotificationEvent,
  resolveDeepLink,
} from "../../../Actions/NotificationActions";
import NotificationToast from "../../../Shared/Components/NotificationToast/NotificationToast";

const NotificationManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [toast, setToast] = useState(null);

  // Tokens rotate, so the current one is pushed to the backend on every launch.
  // Permission is never requested here, only in LoginComponent after a user gesture.
  useEffect(() => {
    syncFcmToken({ requestPermission: false });
  }, []);

  const handleNotificationClick = useCallback(
    (campaignId, deepLink) => {
      const route = resolveDeepLink(deepLink);
      navigate(route);
      reportNotificationEvent(campaignId, NotificationEvent.clickThrough);
    },
    [navigate]
  );

  useEffect(() => {
    let unsubscribe;
    let cancelled = false;

    const subscribe = async () => {
      if (!(await isFcmSupported()) || cancelled) return;

      unsubscribe = onMessage(getMessaging(app), (payload) => {
        const { title, body } = payload.notification || {};
        if (!title) return;

        const campaignId = payload.data?.campaign_id;
        setToast({ title, body, campaignId, deepLink: payload.data?.deep_link });
        reportNotificationEvent(campaignId, NotificationEvent.opened);
      });
    };

    subscribe();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handleMessage = (event) => {
      if (event.data?.type !== NOTIFICATION_CLICKED_MESSAGE) return;

      const { campaignId, deepLink } = event.data;
      reportNotificationEvent(campaignId, NotificationEvent.opened);
      handleNotificationClick(campaignId, deepLink);
    };

    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, [handleNotificationClick]);

  // Cold start: the worker opened a new window with the campaign in the URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const campaignId = params.get(NOTIFICATION_CAMPAIGN_PARAM);
    if (!campaignId) return;

    reportNotificationEvent(campaignId, NotificationEvent.opened);
    reportNotificationEvent(campaignId, NotificationEvent.clickThrough);

    params.delete(NOTIFICATION_CAMPAIGN_PARAM);
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ""}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const closeToast = useCallback(() => setToast(null), []);

  const onToastClick = useCallback(() => {
    const { campaignId, deepLink } = toast || {};
    setToast(null);
    reportNotificationEvent(campaignId, NotificationEvent.clicked);
    handleNotificationClick(campaignId, deepLink);
  }, [toast, handleNotificationClick]);

  if (!toast) return null;

  return (
    <NotificationToast
      title={toast.title}
      body={toast.body}
      onClick={onToastClick}
      close={closeToast}
    />
  );
};

export default NotificationManager;
